import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { ScrollArea } from '../components/ui/scroll-area';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import RichTextEditor from '../components/RichTextEditor';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  Mail,
  Send,
  Inbox,
  RefreshCw,
  Trash2,
  Reply,
  Paperclip,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Users,
  FileText,
  Search,
  MailOpen,
  LogOut,
  Plus,
  X,
  Phone,
  Loader2,
  Clock,
  Menu
} from 'lucide-react';
import { toast } from 'sonner';
import { requestLoginCode, verifyLoginCode, requestSmsOtp, verifySmsOtp } from '../lib/api';
import { 
  Email, 
  EmailDetail, 
  Member, 
  Template, 
  View,
  TEMPLATE_ICONS, 
  TEMPLATE_COLORS, 
  BOARD_ROLES,
  API_BASE,
  sortMembersByApartment,
  SortableTemplateCard
} from '../components/mail-portal';

export default function MailPortal() {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  
  const [view, setView] = useState<View>('inbox');
  const [emails, setEmails] = useState<Email[]>([]);
  const [selectedEmail, setSelectedEmail] = useState<EmailDetail | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [scheduledEmails, setScheduledEmails] = useState<any[]>([]);
  const [sentEmails, setSentEmails] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  // DnD sensors for template reordering
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Compose state
  const [composeTo, setComposeTo] = useState<string[]>([]);
  const [composeCc, setComposeCc] = useState<string[]>([]);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeAttachments, setComposeAttachments] = useState<File[]>([]);
  const [replyToEmail, setReplyToEmail] = useState<EmailDetail | null>(null);
  const [showMemberPicker, setShowMemberPicker] = useState(false);
  const [sendToAll, setSendToAll] = useState(false);
  const [showMemberList, setShowMemberList] = useState(false);
  const [customEmail, setCustomEmail] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  
  // Scheduling state
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  
  // Template editing
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Login state
  const [loginMethod, setLoginMethod] = useState<'email' | 'sms'>('email');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPhone, setLoginPhone] = useState('');
  const [loginCode, setLoginCode] = useState('');
  const [loginStep, setLoginStep] = useState<'input' | 'verify'>('input');
  const [loginLoading, setLoginLoading] = useState(false);
  
  const handleRequestCode = async () => {
    setLoginLoading(true);
    try {
      if (loginMethod === 'email') {
        if (!loginEmail) {
          toast.error('Ange din e-postadress');
          return;
        }
        await requestLoginCode(loginEmail);
        toast.success('Kod skickad till din e-post');
      } else {
        if (!loginPhone) {
          toast.error('Ange ditt telefonnummer');
          return;
        }
        await requestSmsOtp(loginPhone);
        toast.success('Kod skickad via SMS');
      }
      setLoginStep('verify');
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Kunde inte skicka kod');
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleVerifyCode = async (code: string) => {
    if (code.length !== 6) return;
    
    setLoginLoading(true);
    try {
      let result;
      if (loginMethod === 'email') {
        result = await verifyLoginCode(loginEmail, code);
      } else {
        result = await verifySmsOtp(loginPhone, code);
      }
      
      const token = result?.data?.token;
      const userData = result?.data?.user;
      
      if (token && userData) {
        login(token, userData as any);
        toast.success('Inloggad!');
        setLoginCode('');
        setLoginStep('input');
      } else {
        toast.error('Inloggningen misslyckades');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || 'Fel kod');
      setLoginCode('');
    } finally {
      setLoginLoading(false);
    }
  };
  
  useEffect(() => {
    if (user) {
      const userRole = (user.role || '').toLowerCase();
      if (!BOARD_ROLES.some(role => userRole.includes(role))) {
        toast.error('Endast styrelsemedlemmar har tillgång till mail-portalen');
        logout();
      }
    }
  }, [user]);

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/mail/inbox?limit=100`);
      const data = await response.json();
      if (data.emails) {
        setEmails(data.emails);
        // Update unread count
        const unread = data.emails.filter((e: Email) => !e.is_read).length;
        setUnreadCount(unread);
      }
    } catch (error) {
      toast.error('Kunde inte hämta e-post');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMembers = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/members`);
      const data = await response.json();
      if (data.members) {
        setMembers(sortMembersByApartment(data.members));
      }
    } catch (error) {
      console.error('Failed to fetch members:', error);
    }
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/templates`);
      const data = await response.json();
      if (data.templates) {
        setTemplates(data.templates);
      }
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  }, []);

  const fetchScheduledEmails = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/scheduled`);
      const data = await response.json();
      if (data.scheduled_emails) {
        setScheduledEmails(data.scheduled_emails);
      }
    } catch (error) {
      console.error('Failed to fetch scheduled emails:', error);
    }
  }, []);

  const cancelScheduledEmail = async (emailId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/scheduled/${emailId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Schemalagt mail avbrutet');
        fetchScheduledEmails();
      } else {
        toast.error('Kunde inte avbryta');
      }
    } catch (error) {
      toast.error('Kunde inte avbryta');
    }
  };

  const fetchSentEmails = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/sent`);
      const data = await response.json();
      if (data.emails) {
        setSentEmails(data.emails);
      }
    } catch (error) {
      console.error('Failed to fetch sent emails:', error);
    }
  }, []);

  const fetchDrafts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/drafts`);
      const data = await response.json();
      if (data.drafts) {
        setDrafts(data.drafts);
      }
    } catch (error) {
      console.error('Failed to fetch drafts:', error);
    }
  }, []);

  const deleteDraft = async (draftId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/mail/drafts/${draftId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Utkast raderat');
        fetchDrafts();
      }
    } catch (error) {
      toast.error('Kunde inte radera utkast');
    }
  };

  const openDraft = (draft: any) => {
    setComposeTo(draft.to || []);
    setComposeCc(draft.cc || []);
    setComposeSubject(draft.subject || '');
    setComposeBody(draft.body_plain || draft.body_html || '');
    setView('compose');
    // Delete the draft after opening
    deleteDraft(draft.id);
  };

  useEffect(() => {
    fetchEmails();
    fetchMembers();
    fetchTemplates();
    fetchScheduledEmails();
    fetchSentEmails();
    fetchDrafts();
  }, [fetchEmails, fetchMembers, fetchTemplates, fetchScheduledEmails, fetchSentEmails, fetchDrafts]);

  const openEmail = async (emailId: string, folder: string = 'INBOX') => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/mail/email/${emailId}?folder=${encodeURIComponent(folder)}`);
      const data = await response.json();
      if (!data.error) {
        setSelectedEmail(data);
        setView('read');
        
        // Mark as read on the IMAP server (persist the read status)
        if (folder === 'INBOX') {
          try {
            await fetch(`${API_BASE}/api/mail/email/${emailId}/read?folder=${encodeURIComponent(folder)}`, {
              method: 'POST'
            });
          } catch (e) {
            console.error('Failed to mark email as read on server:', e);
          }
          
          // Update local state to mark as read and update unread count
          setEmails(prev => {
            const updated = prev.map(e => 
              e.id === emailId ? { ...e, is_read: true } : e
            );
            // Recalculate unread count
            setUnreadCount(updated.filter(e => !e.is_read).length);
            return updated;
          });
        }
      } else {
        toast.error('Kunde inte öppna e-post');
      }
    } catch (error) {
      toast.error('Kunde inte öppna e-post');
    } finally {
      setLoading(false);
    }
  };

  // Handler for selecting emails from different folders
  const handleSelectEmail = (emailId: string, folder: string = 'INBOX') => {
    setCurrentFolder(folder);
    openEmail(emailId, folder);
  };

  // Track which folder we're viewing for delete operations
  const [currentFolder, setCurrentFolder] = useState<string>('INBOX');

  const deleteEmail = async (emailId: string, folder: string = currentFolder) => {
    if (!confirm('Är du säker på att du vill radera detta mail?')) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/mail/email/${emailId}?folder=${encodeURIComponent(folder)}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        toast.success('E-post raderad');
        // Update the correct list based on folder
        if (folder === 'INBOX') {
          setEmails(prev => {
            const updated = prev.filter(e => e.id !== emailId);
            // Recalculate unread count
            setUnreadCount(updated.filter(e => !e.is_read).length);
            return updated;
          });
        } else if (folder === 'INBOX.Sent') {
          setSentEmails(prev => prev.filter(e => e.id !== emailId));
        }
        if (selectedEmail?.id === emailId) {
          setSelectedEmail(null);
          setView(folder === 'INBOX.Sent' ? 'sent' : 'inbox');
        }
      }
    } catch (error) {
      toast.error('Kunde inte radera e-post');
    }
  };

  const handleReply = () => {
    if (!selectedEmail) return;
    
    setReplyToEmail(selectedEmail);
    
    // Extract email from "Name <email>" format
    const fromMatch = selectedEmail.from.match(/<([^>]+)>/);
    const replyEmail = fromMatch ? fromMatch[1] : selectedEmail.from;
    
    setComposeTo([replyEmail]);
    setComposeSubject(selectedEmail.subject.startsWith('Re:') 
      ? selectedEmail.subject 
      : `Re: ${selectedEmail.subject}`
    );
    setComposeBody(`\n\n---\nFrån: ${selectedEmail.from}\nDatum: ${new Date(selectedEmail.date).toLocaleString('sv-SE')}\n\n${selectedEmail.body.plain || ''}`);
    setView('compose');
  };

  const handleSendEmail = async () => {
    // Get sender name from logged in user
    const senderName = user?.name || user?.email;
    
    // Generate HTML body - use template if selected, otherwise simple formatting
    const bodyHtml = selectedTemplate 
      ? generateTemplateHtml(selectedTemplate, composeBody, senderName)
      : composeBody.replace(/\n/g, '<br>');

    // Build schedule datetime if enabled
    const scheduledFor = scheduleEnabled && scheduleDate 
      ? `${scheduleDate}T${scheduleTime}:00`
      : undefined;

    // Check if we have attachments
    const hasAttachments = composeAttachments.length > 0;

    if (sendToAll) {
      // Send to all members
      if (hasAttachments) {
        // With attachments: send individual emails with delay
        if (scheduledFor) {
          toast.error('Schemalagda mail med bilagor stöds inte ännu');
          return;
        }
        
        setLoading(true);
        try {
          const formData = new FormData();
          formData.append('subject', composeSubject);
          formData.append('body_html', bodyHtml);
          formData.append('body_plain', composeBody);
          formData.append('recipient_filter', 'all');
          
          // Add attachments
          composeAttachments.forEach((file) => {
            formData.append('attachments', file);
          });
          
          const response = await fetch(`${API_BASE}/api/mail/send-to-members-with-attachments`, {
            method: 'POST',
            body: formData
          });
          
          const data = await response.json();
          if (data.success) {
            toast.success(data.message || `Skickar e-post till ${data.recipients_count} medlemmar...`);
            resetCompose();
            setView('inbox');
          } else {
            toast.error(data.error || 'Kunde inte skicka e-post');
          }
        } catch (error) {
          toast.error('Kunde inte skicka e-post');
        } finally {
          setLoading(false);
        }
      } else {
        // Without attachments: use BCC (faster)
        setLoading(true);
        try {
          const response = await fetch(`${API_BASE}/api/mail/send-to-members`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              subject: composeSubject,
              body_html: bodyHtml,
              body_plain: composeBody,
              recipient_filter: 'all',
              scheduled_for: scheduledFor
            })
          });
          
          const data = await response.json();
          if (data.success) {
            if (scheduledFor) {
              toast.success(`E-post schemalagd för ${data.recipients_count} medlemmar`);
            } else {
              toast.success(`E-post skickad till ${data.recipients_count} medlemmar`);
            }
            resetCompose();
            setView('inbox');
          } else {
            toast.error(data.error || 'Kunde inte skicka e-post');
          }
        } catch (error) {
          toast.error('Kunde inte skicka e-post');
        } finally {
          setLoading(false);
        }
      }
    } else {
      // Send to specific recipients
      if (composeTo.length === 0) {
        toast.error('Ange minst en mottagare');
        return;
      }
      
      setLoading(true);
      try {
        let response;
        
        if (hasAttachments) {
          // Use FormData for file uploads
          const formData = new FormData();
          formData.append('to', JSON.stringify(composeTo));
          if (composeCc.length > 0) {
            formData.append('cc', JSON.stringify(composeCc));
          }
          formData.append('subject', composeSubject);
          formData.append('body_html', bodyHtml);
          formData.append('body_plain', composeBody);
          if (replyToEmail?.message_id) {
            formData.append('reply_to_message_id', replyToEmail.message_id);
          }
          if (replyToEmail?.references || replyToEmail?.message_id) {
            formData.append('references', replyToEmail.references || replyToEmail.message_id || '');
          }
          if (scheduledFor) {
            formData.append('scheduled_for', scheduledFor);
          }
          
          // Add attachments
          composeAttachments.forEach((file) => {
            formData.append('attachments', file);
          });
          
          response = await fetch(`${API_BASE}/api/mail/send-with-attachments`, {
            method: 'POST',
            body: formData
          });
        } else {
          // Use JSON for emails without attachments
          response = await fetch(`${API_BASE}/api/mail/send`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: composeTo,
              cc: composeCc.length > 0 ? composeCc : undefined,
              subject: composeSubject,
              body_html: bodyHtml,
              body_plain: composeBody,
              reply_to_message_id: replyToEmail?.message_id,
              references: replyToEmail?.references || replyToEmail?.message_id,
              scheduled_for: scheduledFor
            })
          });
        }
        
        const data = await response.json();
        if (data.success) {
          if (scheduledFor) {
            toast.success('E-post schemalagd!');
          } else {
            toast.success('E-post skickad!');
          }
          resetCompose();
          setView('inbox');
        } else {
          toast.error(data.error || 'Kunde inte skicka e-post');
        }
      } catch (error) {
        toast.error('Kunde inte skicka e-post');
      } finally {
        setLoading(false);
      }
    }
  };

  const resetCompose = () => {
    setComposeTo([]);
    setComposeCc([]);
    setComposeSubject('');
    setComposeBody('');
    setComposeAttachments([]);
    setReplyToEmail(null);
    setSendToAll(false);
    setShowMemberList(false);
    setCustomEmail('');
    setSelectedTemplate(null);
    setScheduleEnabled(false);
    setScheduleDate('');
    setScheduleTime('09:00');
  };

  const handleNewCompose = () => {
    resetCompose();
    setView('compose');
  };

  const saveTemplate = async () => {
    if (!editingTemplate?.name || !editingTemplate?.subject) {
      toast.error('Ange namn och ämne för mallen');
      return;
    }
    
    try {
      const method = editingTemplate.id ? 'PUT' : 'POST';
      const url = editingTemplate.id 
        ? `${API_BASE}/api/mail/templates/${editingTemplate.id}`
        : `${API_BASE}/api/mail/templates`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingTemplate)
      });
      
      const data = await response.json();
      if (data.id) {
        toast.success('Mall sparad');
        fetchTemplates();
        setEditingTemplate(null);
      }
    } catch (error) {
      toast.error('Kunde inte spara mall');
    }
  };

  const deleteTemplate = async (templateId: string) => {
    if (!confirm('Radera denna mall?')) return;
    
    try {
      await fetch(`${API_BASE}/api/mail/templates/${templateId}`, {
        method: 'DELETE'
      });
      toast.success('Mall raderad');
      fetchTemplates();
    } catch (error) {
      toast.error('Kunde inte radera mall');
    }
  };

  // Handle template drag and drop reordering
  const handleTemplateDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = templates.findIndex(t => t.id === active.id);
      const newIndex = templates.findIndex(t => t.id === over.id);
      
      const newTemplates = arrayMove(templates, oldIndex, newIndex);
      setTemplates(newTemplates);
      
      // Save new order to backend
      try {
        await fetch(`${API_BASE}/api/mail/templates/reorder`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            template_ids: newTemplates.map(t => t.id) 
          })
        });
      } catch (error) {
        console.error('Failed to save template order:', error);
        // Revert on error
        fetchTemplates();
      }
    }
  };

  // Generate HTML email from template
  const generateTemplateHtml = (template: Template, bodyContent: string, senderName?: string): string => {
    const colorObj = TEMPLATE_COLORS.find(c => c.id === template.header_color) || TEMPLATE_COLORS[0];
    const iconObj = TEMPLATE_ICONS.find(i => i.id === template.icon_id);
    
    // Use emoji inside a circular div for perfect circle in email clients
    const iconHtml = iconObj && iconObj.icon && iconObj.icon !== '—' ? `
      <td style="width: 56px; padding: 0; vertical-align: middle;">
        <div style="width: 56px; height: 56px; background: white; border-radius: 28px; text-align: center; line-height: 56px; font-size: 28px;">
          ${iconObj.icon}
        </div>
      </td>
      <td style="width: 16px;"></td>
    ` : '';

    const senderInfo = senderName 
      ? `Detta mail skickades från styrelsen@grindstugatan.se genom ${senderName}`
      : `Detta mail skickades från styrelsen@grindstugatan.se`;

    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" style="max-width: 600px; margin: 0 auto; background: white;">
    <!-- Header -->
    <tr>
      <td style="background: linear-gradient(135deg, ${colorObj.color}, ${colorObj.color}dd); padding: 32px 24px;">
        <table width="100%" cellpadding="0" cellspacing="0">
          <tr>
            ${iconHtml}
            <td style="vertical-align: middle;">
              <h1 style="margin: 0; color: white; font-size: 24px; font-weight: 600;">${template.subject}</h1>
              <p style="margin: 8px 0 0; color: rgba(255,255,255,0.9); font-size: 14px;">BRF Grindstugatan 1</p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    <!-- Content -->
    <tr>
      <td style="padding: 32px 24px;">
        <div style="color: #374151; font-size: 16px; line-height: 1.6;">
          ${bodyContent}
        </div>
      </td>
    </tr>
    <!-- Footer -->
    <tr>
      <td style="background: ${colorObj.light}; padding: 24px; text-align: center; border-top: 1px solid ${colorObj.color}22;">
        <p style="margin: 0; color: #6b7280; font-size: 14px;">
          Med vänliga hälsningar,<br>
          <strong style="color: ${colorObj.color};">Styrelsen BRF Grindstugatan 1</strong>
        </p>
        <p style="margin: 16px 0 0; color: #9ca3af; font-size: 12px;">
          ${senderInfo}
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const applyTemplate = (template: Template) => {
    setComposeSubject(template.subject);
    // Store template info for later HTML generation
    const bodyContent = template.body_plain || template.body_html.replace(/<[^>]*>/g, '');
    setComposeBody(bodyContent);
    // Store selected template for HTML generation when sending
    setSelectedTemplate(template);
    setView('compose');
  };

  const downloadAttachment = (attachment: { filename: string; data: string; content_type: string }) => {
    const link = document.createElement('a');
    link.href = `data:${attachment.content_type};base64,${attachment.data}`;
    link.download = attachment.filename;
    link.click();
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
  };

  const filteredEmails = emails.filter(email =>
    email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    email.from.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const userRole = (user?.role || '').toLowerCase();
  
  // Show login screen if not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur">
          <CardHeader className="text-center space-y-4">
            <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <div>
              <CardTitle className="text-2xl">Styrelsemail</CardTitle>
              <CardDescription className="mt-2">
                BRF Grindstugatan 1
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {loginStep === 'input' ? (
              <>
                {/* Method selector */}
                <div className="flex gap-2">
                  <Button
                    variant={loginMethod === 'email' ? 'default' : 'outline'}
                    className={`flex-1 ${loginMethod === 'email' ? 'bg-blue-600' : ''}`}
                    onClick={() => setLoginMethod('email')}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    E-post
                  </Button>
                  <Button
                    variant={loginMethod === 'sms' ? 'default' : 'outline'}
                    className={`flex-1 ${loginMethod === 'sms' ? 'bg-blue-600' : ''}`}
                    onClick={() => setLoginMethod('sms')}
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    SMS
                  </Button>
                </div>
                
                {/* Input field */}
                {loginMethod === 'email' ? (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">E-postadress</label>
                    <Input
                      type="email"
                      placeholder="din@email.se"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRequestCode()}
                      data-testid="mail-login-email"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Telefonnummer</label>
                    <Input
                      type="tel"
                      placeholder="070-123 45 67"
                      value={loginPhone}
                      onChange={(e) => setLoginPhone(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleRequestCode()}
                      data-testid="mail-login-phone"
                    />
                  </div>
                )}
                
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={handleRequestCode}
                  disabled={loginLoading}
                  data-testid="mail-login-submit"
                >
                  {loginLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Skicka kod
                </Button>
                
                <p className="text-xs text-slate-500 text-center">
                  Endast styrelsemedlemmar har tillgång till mail-portalen
                </p>
              </>
            ) : (
              <>
                <div className="text-center space-y-2">
                  <p className="text-sm text-slate-600">
                    Kod skickad till {loginMethod === 'email' ? loginEmail : loginPhone}
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={loginCode}
                    onChange={(value) => {
                      setLoginCode(value);
                      if (value.length === 6) {
                        handleVerifyCode(value);
                      }
                    }}
                    data-testid="mail-login-otp"
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                
                {loginLoading && (
                  <div className="flex justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  </div>
                )}
                
                <Button 
                  variant="ghost"
                  className="w-full"
                  onClick={() => {
                    setLoginStep('input');
                    setLoginCode('');
                  }}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Tillbaka
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Check if user has board access
  if (!BOARD_ROLES.some(role => userRole.includes(role))) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <X className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <CardTitle>Åtkomst nekad</CardTitle>
            <CardDescription>
              Endast styrelsemedlemmar har tillgång till mail-portalen
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              className="w-full"
              variant="outline"
              onClick={() => logout()}
            >
              Logga ut
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row" data-testid="mail-portal">
      {/* Mobile Header */}
      <div className="md:hidden bg-white border-b border-slate-200 p-4 flex items-center justify-between sticky top-0 z-40">
        <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <Mail className="w-5 h-5" />
          Styrelsemail
        </h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleNewCompose}
            className="bg-blue-600 text-white hover:bg-blue-700 border-0"
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <Menu className="w-5 h-5" />
          </Button>
        </div>
      </div>
      
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      
      {/* Sidebar - Hidden on mobile unless menu is open */}
      <div className={`
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
        fixed md:relative
        inset-y-0 left-0
        w-64 bg-white border-r border-slate-200 flex flex-col
        z-50 md:z-auto
        transition-transform duration-200 ease-in-out
      `}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Styrelsemail
            </h1>
            <p className="text-sm text-slate-500 mt-1">styrelsen@grindstugatan.se</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="p-3">
          <Button 
            onClick={() => { handleNewCompose(); setMobileMenuOpen(false); }}
            className="w-full bg-blue-600 hover:bg-blue-700"
            data-testid="compose-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Skriv nytt
          </Button>
        </div>
        
        <nav className="flex-1 p-2">
          <button
            onClick={() => { setView('inbox'); fetchEmails(); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left ${
              view === 'inbox' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="inbox-nav"
          >
            <Inbox className="w-5 h-5" />
            <span>Inkorg</span>
            {unreadCount > 0 && (
              <Badge className="ml-auto bg-blue-600">{unreadCount}</Badge>
            )}
          </button>
          
          <button
            onClick={() => { setView('sent'); fetchSentEmails(); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left mt-1 ${
              view === 'sent' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="sent-nav"
          >
            <Send className="w-5 h-5" />
            <span>Skickat</span>
          </button>
          
          <button
            onClick={() => { setView('drafts'); fetchDrafts(); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left mt-1 ${
              view === 'drafts' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="drafts-nav"
          >
            <FileText className="w-5 h-5" />
            <span>Utkast</span>
            {drafts.length > 0 && (
              <Badge variant="outline" className="ml-auto">{drafts.length}</Badge>
            )}
          </button>
          
          <button
            onClick={() => { setView('templates'); fetchTemplates(); setMobileMenuOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left mt-1 ${
              view === 'templates' ? 'bg-blue-50 text-blue-700' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid="templates-nav"
          >
            <FileText className="w-5 h-5" />
            <span>Mallar</span>
          </button>
          
          {/* Scheduled emails section */}
          {scheduledEmails.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2 px-3 mb-2">
                <Clock className="w-4 h-4 text-orange-500" />
                <span className="text-sm font-medium text-slate-700">Schemalagda ({scheduledEmails.length})</span>
              </div>
              <div className="space-y-1">
                {scheduledEmails.slice(0, 3).map((email) => (
                  <div
                    key={email.id}
                    className="px-3 py-2 bg-orange-50 rounded-lg text-sm"
                  >
                    <div className="font-medium text-slate-700 truncate">{email.subject}</div>
                    <div className="text-xs text-orange-600 flex items-center justify-between">
                      <span>
                        {new Date(email.scheduled_for).toLocaleDateString('sv-SE', { 
                          day: 'numeric', 
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                      <button
                        onClick={() => cancelScheduledEmail(email.id)}
                        className="text-red-500 hover:text-red-700"
                        title="Avbryt"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
                {scheduledEmails.length > 3 && (
                  <div className="text-xs text-slate-500 px-3">
                    +{scheduledEmails.length - 3} fler...
                  </div>
                )}
              </div>
            </div>
          )}
        </nav>
        
        <div className="p-3 border-t border-slate-200">
          <div className="text-sm text-slate-600 mb-2">
            Inloggad som: {user.name || user.email}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="w-full"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logga ut
          </Button>
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 md:ml-0">
        {/* Inbox View */}
        {view === 'inbox' && (
          <>
            <div className="p-3 md:p-4 bg-white border-b border-slate-200 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1 sm:max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Sök i inkorg..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button
                variant="outline"
                onClick={fetchEmails}
                disabled={loading}
                data-testid="refresh-btn"
                className="sm:ml-auto"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Uppdatera
              </Button>
            </div>
            
            <ScrollArea className="flex-1">
              <div className="divide-y divide-slate-100">
                {filteredEmails.map((email) => (
                  <div
                    key={email.id}
                    onClick={() => handleSelectEmail(email.id, 'INBOX')}
                    className={`p-4 hover:bg-slate-50 cursor-pointer flex items-start gap-4 ${
                      !email.is_read ? 'bg-blue-50/50' : ''
                    }`}
                    data-testid={`email-item-${email.id}`}
                  >
                    <div className="flex-shrink-0 mt-1">
                      {email.is_read ? (
                        <MailOpen className="w-5 h-5 text-slate-400" />
                      ) : (
                        <Mail className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-medium truncate ${!email.is_read ? 'text-slate-900' : 'text-slate-600'}`}>
                          {email.from.replace(/<.*>/, '').trim() || email.from}
                        </span>
                        {email.has_attachments && (
                          <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                      <div className={`truncate ${!email.is_read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                        {email.subject}
                      </div>
                    </div>
                    <div className="text-sm text-slate-500 flex-shrink-0">
                      {formatDate(email.date)}
                    </div>
                  </div>
                ))}
                
                {filteredEmails.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    {searchQuery ? 'Inga träffar' : 'Inkorgen är tom'}
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
        
        {/* Sent Emails View */}
        {view === 'sent' && (
          <>
            <div className="p-4 bg-white border-b border-slate-200 flex items-center gap-4">
              <h2 className="text-lg font-semibold">Skickade mail</h2>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={fetchSentEmails}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Uppdatera
              </Button>
            </div>
            
            <ScrollArea className="flex-1">
              <div className="divide-y divide-slate-100">
                {sentEmails.map((email) => {
                  // Handle both IMAP 'date' and CommHub 'sent_at' fields
                  const emailDate = email.sent_at || email.date;
                  const dateDisplay = emailDate ? new Date(emailDate).toLocaleDateString('sv-SE', {
                    day: 'numeric',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  }) : '';
                  
                  return (
                    <div
                      key={email.id}
                      className="p-4 hover:bg-slate-50 cursor-pointer"
                      onClick={() => handleSelectEmail(email.id, 'INBOX.Sent')}
                      data-testid={`sent-email-${email.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-slate-700">
                              Till: {email.type === 'broadcast' 
                                ? `${email.recipients_count} medlemmar` 
                                : (Array.isArray(email.to) ? email.to.join(', ') : email.to)}
                            </span>
                          </div>
                          <div className="font-medium text-slate-800 truncate">
                            {email.subject}
                          </div>
                          <div className="text-sm text-slate-500 truncate mt-1">
                            {email.body_plain?.substring(0, 100) || email.subject || 'Klicka för att visa'}
                          </div>
                        </div>
                        <div className="text-sm text-slate-500 flex-shrink-0">
                          {dateDisplay}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {sentEmails.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <Send className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Inga skickade mail ännu</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
        
        {/* Drafts View */}
        {view === 'drafts' && (
          <>
            <div className="p-4 bg-white border-b border-slate-200 flex items-center gap-4">
              <h2 className="text-lg font-semibold">Utkast</h2>
              <div className="flex-1" />
              <Button
                variant="outline"
                onClick={fetchDrafts}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Uppdatera
              </Button>
            </div>
            
            <ScrollArea className="flex-1">
              <div className="divide-y divide-slate-100">
                {drafts.map((draft) => (
                  <div
                    key={draft.id}
                    className="p-4 hover:bg-slate-50 cursor-pointer flex items-center gap-3"
                    onClick={() => openDraft(draft)}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 truncate">
                        {draft.subject || '(Inget ämne)'}
                      </div>
                      <div className="text-sm text-slate-500 truncate mt-1">
                        {draft.body_plain?.substring(0, 100) || 'Tomt utkast'}
                      </div>
                    </div>
                    <div className="text-sm text-slate-500 flex-shrink-0">
                      {new Date(draft.updated_at).toLocaleDateString('sv-SE', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteDraft(draft.id);
                      }}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                {drafts.length === 0 && (
                  <div className="p-8 text-center text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>Inga utkast</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </>
        )}
        
        {/* Read Email View */}
        {view === 'read' && selectedEmail && (
          <>
            <div className="p-4 bg-white border-b border-slate-200 flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setSelectedEmail(null);
                  setView('inbox');
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Tillbaka
              </Button>
              <div className="flex-1" />
              <Button variant="outline" onClick={handleReply}>
                <Reply className="w-4 h-4 mr-2" />
                Svara
              </Button>
              <Button
                variant="outline"
                onClick={() => deleteEmail(selectedEmail.id)}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
            
            <ScrollArea className="flex-1 p-6">
              <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold text-slate-800 mb-4">
                  {selectedEmail.subject}
                </h1>
                
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-medium text-slate-800">{selectedEmail.from}</div>
                      <div className="text-sm text-slate-500">Till: {selectedEmail.to}</div>
                      {selectedEmail.cc && (
                        <div className="text-sm text-slate-500">Kopia: {selectedEmail.cc}</div>
                      )}
                    </div>
                    <div className="text-sm text-slate-500">
                      {new Date(selectedEmail.date).toLocaleString('sv-SE')}
                    </div>
                  </div>
                </div>
                
                {/* Attachments */}
                {selectedEmail.attachments.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-slate-600 mb-2">
                      Bilagor ({selectedEmail.attachments.length})
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedEmail.attachments.map((att, idx) => (
                        <Button
                          key={idx}
                          variant="outline"
                          size="sm"
                          onClick={() => downloadAttachment(att)}
                          className="flex items-center gap-2"
                        >
                          <Paperclip className="w-4 h-4" />
                          {att.filename}
                          <span className="text-slate-400">
                            ({Math.round(att.size / 1024)} KB)
                          </span>
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Email body */}
                <div className="prose prose-slate max-w-none">
                  {selectedEmail.body.html ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedEmail.body.html }} />
                  ) : (
                    <pre className="whitespace-pre-wrap font-sans">{selectedEmail.body.plain}</pre>
                  )}
                </div>
              </div>
            </ScrollArea>
          </>
        )}
        
        {/* Compose View */}
        {view === 'compose' && (
          <>
            <div className="p-3 md:p-4 bg-white border-b border-slate-200 flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  resetCompose();
                  setView('inbox');
                }}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">Avbryt</span>
              </Button>
              <div className="flex-1" />
              <Button
                onClick={handleSendEmail}
                disabled={loading || (!sendToAll && composeTo.length === 0)}
                className={`${scheduleEnabled ? "bg-orange-600 hover:bg-orange-700" : "bg-blue-600 hover:bg-blue-700"}`}
                data-testid="send-btn"
                size="sm"
              >
                {scheduleEnabled ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    {loading ? 'Schemalägger...' : 'Schemalägg'}
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? 'Skickar...' : 'Skicka'}
                  </>
                )}
              </Button>
            </div>
            
            <ScrollArea className="flex-1 p-3 md:p-6">
              <div className="max-w-4xl mx-auto space-y-4">
                {/* Send to all toggle with expandable member list */}
                <div className="bg-blue-50 rounded-lg overflow-hidden">
                  <div className="flex items-center gap-2 p-3">
                    <input
                      type="checkbox"
                      id="sendToAll"
                      checked={sendToAll}
                      onChange={(e) => setSendToAll(e.target.checked)}
                      className="w-4 h-4 flex-shrink-0"
                    />
                    <label htmlFor="sendToAll" className="text-sm font-medium text-blue-800 flex-1 min-w-0">
                      Skicka till alla medlemmar ({members.length} st)
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowMemberList(!showMemberList)}
                      className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 flex-shrink-0"
                      data-testid="toggle-member-list"
                    >
                      {showMemberList ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  
                  {/* Expandable member list */}
                  {showMemberList && (
                    <div className="border-t border-blue-100 p-3 bg-white/50 max-h-64 overflow-y-auto">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {members.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center gap-2 text-sm p-2 bg-white rounded border border-blue-100"
                          >
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                              <span className="text-xs font-bold text-blue-700">
                                {member.apartment_id || '?'}
                              </span>
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-700 truncate">
                                {member.name || 'Okänt namn'}
                              </div>
                              <div className="text-slate-500 text-xs truncate">
                                {member.email}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Use template button */}
                <Button
                  variant="outline"
                  onClick={() => setShowTemplatePicker(true)}
                  className="w-full justify-start"
                  data-testid="use-template-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Använd mall
                </Button>
                
                {/* Recipients */}
                {!sendToAll && (
                  <div>
                    <label className="text-sm font-medium text-slate-700">Till</label>
                    <div className="flex flex-wrap gap-2 p-2 border rounded-lg mt-1 min-h-[42px]">
                      {composeTo.map((email, idx) => (
                        <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                          {email}
                          <X
                            className="w-3 h-3 cursor-pointer"
                            onClick={() => setComposeTo(prev => prev.filter((_, i) => i !== idx))}
                          />
                        </Badge>
                      ))}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowMemberPicker(true)}
                        className="h-6"
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Lägg till medlem
                      </Button>
                    </div>
                    
                    {/* Custom email input */}
                    <div className="mt-2 flex gap-2">
                      <Input
                        type="email"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        placeholder="Ange egen e-postadress..."
                        className="flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && customEmail) {
                            e.preventDefault();
                            // Basic email validation
                            if (customEmail.includes('@') && customEmail.includes('.')) {
                              if (!composeTo.includes(customEmail)) {
                                setComposeTo(prev => [...prev, customEmail]);
                              }
                              setCustomEmail('');
                            } else {
                              toast.error('Ogiltig e-postadress');
                            }
                          }
                        }}
                        data-testid="custom-email-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => {
                          if (customEmail && customEmail.includes('@') && customEmail.includes('.')) {
                            if (!composeTo.includes(customEmail)) {
                              setComposeTo(prev => [...prev, customEmail]);
                            }
                            setCustomEmail('');
                          } else {
                            toast.error('Ogiltig e-postadress');
                          }
                        }}
                        disabled={!customEmail}
                        data-testid="add-custom-email-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Lägg till
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Lägg till e-postadress som inte finns i medlemsregistret
                    </p>
                  </div>
                )}
                
                {/* Selected template indicator */}
                {selectedTemplate && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm"
                      style={{ background: TEMPLATE_COLORS.find(c => c.id === selectedTemplate.header_color)?.color || '#2563eb' }}
                    >
                      {TEMPLATE_ICONS.find(i => i.id === selectedTemplate.icon_id)?.icon || '📧'}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">
                        Mall: {selectedTemplate.name}
                      </p>
                      <p className="text-xs text-green-600">
                        Mailet skickas med grafisk design
                      </p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setSelectedTemplate(null)}
                      className="text-green-700 hover:text-green-800 hover:bg-green-100"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
                
                {/* Subject */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Ämne</label>
                  <Input
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    placeholder="Ämne"
                    className="mt-1"
                    data-testid="compose-subject"
                  />
                </div>
                
                {/* Body */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Meddelande</label>
                  <div className="mt-1">
                    <RichTextEditor
                      content={composeBody}
                      onChange={setComposeBody}
                      placeholder="Skriv ditt meddelande..."
                    />
                  </div>
                </div>

                {/* Attachments */}
                <div>
                  <label className="text-sm font-medium text-slate-700">Bilagor</label>
                  <div className="mt-1 border-2 border-dashed border-slate-200 rounded-lg p-4">
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        if (e.target.files) {
                          const newFiles = Array.from(e.target.files);
                          setComposeAttachments(prev => [...prev, ...newFiles]);
                        }
                      }}
                      className="hidden"
                      id="attachment-input"
                      data-testid="attachment-input"
                    />
                    <label
                      htmlFor="attachment-input"
                      className="block cursor-pointer py-4 text-slate-500 hover:text-slate-700"
                    >
                      <div className="flex flex-col items-center justify-center text-center">
                        <Paperclip className="w-8 h-8 mb-2" />
                        <span className="text-sm font-medium">Klicka för att välja filer</span>
                        <span className="text-xs mt-1">eller dra och släpp här</span>
                      </div>
                    </label>
                    
                    {composeAttachments.length > 0 && (
                      <div className="mt-4 space-y-2 border-t pt-4">
                        {composeAttachments.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              <span className="text-sm truncate">{file.name}</span>
                              <span className="text-xs text-slate-400">
                                ({(file.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setComposeAttachments(prev => prev.filter((_, i) => i !== idx))}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Schedule sending */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="scheduleEmail"
                      checked={scheduleEnabled}
                      onChange={(e) => {
                        setScheduleEnabled(e.target.checked);
                        if (e.target.checked && !scheduleDate) {
                          // Default to tomorrow
                          const tomorrow = new Date();
                          tomorrow.setDate(tomorrow.getDate() + 1);
                          setScheduleDate(tomorrow.toISOString().split('T')[0]);
                        }
                      }}
                      className="w-4 h-4"
                    />
                    <label htmlFor="scheduleEmail" className="text-sm font-medium text-slate-700">
                      Schemalägg utskick
                    </label>
                  </div>
                  
                  {scheduleEnabled && (
                    <div className="flex gap-4 pl-7">
                      <div className="flex-1">
                        <label className="text-xs text-slate-500 block mb-1">Datum</label>
                        <Input
                          type="date"
                          value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="w-full"
                          data-testid="schedule-date"
                        />
                      </div>
                      <div className="w-32">
                        <label className="text-xs text-slate-500 block mb-1">Tid</label>
                        <Input
                          type="time"
                          value={scheduleTime}
                          onChange={(e) => setScheduleTime(e.target.value)}
                          className="w-full"
                          data-testid="schedule-time"
                        />
                      </div>
                    </div>
                  )}
                  
                  {scheduleEnabled && scheduleDate && (
                    <p className="text-xs text-slate-500 pl-7">
                      Mailet skickas {new Date(scheduleDate + 'T' + scheduleTime).toLocaleDateString('sv-SE', { 
                        weekday: 'long', 
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  )}
                </div>
              </div>
            </ScrollArea>
            
            {/* Member Picker Modal */}
            {showMemberPicker && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md max-h-[80vh] flex flex-col">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Välj mottagare</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowMemberPicker(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-auto">
                    <div className="space-y-4">
                      {/* Styrelsemedlemmar */}
                      {members.filter(m => ['board', 'board_member', 'admin', 'styrelse'].includes((m.role || '').toLowerCase())).length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-slate-500 mb-2 px-1">Styrelsemedlemmar</h4>
                          <div className="space-y-1">
                            {members
                              .filter(m => ['board', 'board_member', 'admin', 'styrelse'].includes((m.role || '').toLowerCase()))
                              .map((member) => {
                                const role = (member.role || '').toLowerCase();
                                const roleLabel = role === 'admin' ? 'Admin' : 'Styrelse';
                                const roleColor = role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700';
                                
                                return (
                                  <div
                                    key={member.id}
                                    onClick={() => {
                                      if (!composeTo.includes(member.email)) {
                                        setComposeTo(prev => [...prev, member.email]);
                                      }
                                      setShowMemberPicker(false);
                                    }}
                                    className="p-3 hover:bg-blue-50 rounded-lg cursor-pointer flex items-center justify-between border border-blue-100"
                                  >
                                    <div>
                                      <div className="font-medium">{member.name || member.email}</div>
                                      <div className="text-sm text-slate-500">{member.email}</div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {member.apartment_id && (
                                        <Badge variant="outline">{member.apartment_id}</Badge>
                                      )}
                                      <Badge className={roleColor}>{roleLabel}</Badge>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                      
                      {/* Övriga boende */}
                      <div>
                        <h4 className="text-sm font-semibold text-slate-500 mb-2 px-1">Boende</h4>
                        <div className="space-y-1">
                          {members
                            .filter(m => !['board', 'board_member', 'admin', 'styrelse'].includes((m.role || '').toLowerCase()))
                            .sort((a, b) => (a.apartment_id || '').localeCompare(b.apartment_id || ''))
                            .map((member) => (
                              <div
                                key={member.id}
                                onClick={() => {
                                  if (!composeTo.includes(member.email)) {
                                    setComposeTo(prev => [...prev, member.email]);
                                  }
                                  setShowMemberPicker(false);
                                }}
                                className="p-3 hover:bg-slate-50 rounded-lg cursor-pointer flex items-center justify-between"
                              >
                                <div>
                                  <div className="font-medium">{member.name || member.email}</div>
                                  <div className="text-sm text-slate-500">{member.email}</div>
                                </div>
                                {member.apartment_id && (
                                  <Badge variant="outline">{member.apartment_id}</Badge>
                                )}
                              </div>
                            ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Template Picker Modal */}
            {showTemplatePicker && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-2xl max-h-[80vh] flex flex-col">
                  <CardHeader className="flex flex-row items-center justify-between border-b">
                    <CardTitle>Välj mall</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowTemplatePicker(false)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-auto p-4">
                    {templates.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="font-medium">Inga mallar</p>
                        <p className="text-sm mt-1">Skapa mallar under &quot;Mallar&quot; i menyn</p>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {templates.map((template) => {
                          const colorObj = TEMPLATE_COLORS.find(c => c.id === template.header_color) || TEMPLATE_COLORS[0];
                          const iconObj = TEMPLATE_ICONS.find(i => i.id === template.icon_id);
                          
                          return (
                            <div
                              key={template.id}
                              onClick={() => {
                                applyTemplate(template);
                                setShowTemplatePicker(false);
                              }}
                              className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                            >
                              <div 
                                className="p-3 flex items-center gap-3"
                                style={{ background: `linear-gradient(135deg, ${colorObj.color}, ${colorObj.color}dd)` }}
                              >
                                {iconObj && iconObj.icon !== '—' && (
                                  <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center text-xl flex-shrink-0">
                                    {iconObj.icon}
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-semibold text-white truncate">{template.name}</h3>
                                  <p className="text-white/80 text-sm truncate">{template.subject}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}
        
        {/* Templates View */}
        {view === 'templates' && (
          <>
            <div className="p-4 bg-white border-b border-slate-200 flex items-center gap-2">
              <h2 className="text-lg font-semibold">E-postmallar</h2>
              <div className="flex-1" />
              <Button
                onClick={() => setEditingTemplate({ id: '', name: '', subject: '', body_html: '', icon_id: 'info', header_color: 'blue' })}
              >
                <Plus className="w-4 h-4 mr-2" />
                Ny mall
              </Button>
            </div>
            
            <ScrollArea className="flex-1 p-6">
              <div className="max-w-4xl mx-auto grid gap-4">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleTemplateDragEnd}
                >
                  <SortableContext
                    items={templates.map(t => t.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {templates.map((template) => (
                      <SortableTemplateCard
                        key={template.id}
                        template={template}
                        onApply={applyTemplate}
                        onEdit={setEditingTemplate}
                        onDelete={deleteTemplate}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                
                {templates.length === 0 && (
                  <div className="text-center text-slate-500 py-12">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="font-medium">Inga mallar ännu</p>
                    <p className="text-sm mt-1">Skapa din första mall för att snabbt skicka professionella utskick</p>
                  </div>
                )}
              </div>
            </ScrollArea>
            
            {/* Template Editor Modal */}
            {editingTemplate && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                <Card className="w-full max-w-4xl max-h-[95vh] flex flex-col">
                  <CardHeader className="flex flex-row items-center justify-between border-b">
                    <CardTitle>{editingTemplate.id ? 'Redigera mall' : 'Skapa ny mall'}</CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setEditingTemplate(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent className="flex-1 overflow-auto p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Left column: Settings */}
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">Mallnamn</label>
                          <Input
                            value={editingTemplate.name}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                            placeholder="T.ex. Kallelse till årsmöte"
                            className="mt-1"
                          />
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium">Ämne</label>
                          <Input
                            value={editingTemplate.subject}
                            onChange={(e) => setEditingTemplate({ ...editingTemplate, subject: e.target.value })}
                            placeholder="E-postens ämne"
                            className="mt-1"
                          />
                        </div>
                        
                        {/* Icon picker */}
                        <div>
                          <label className="text-sm font-medium">Ikon</label>
                          <div className="grid grid-cols-6 gap-2 mt-2">
                            {TEMPLATE_ICONS.map((icon) => (
                              <button
                                key={icon.id}
                                onClick={() => setEditingTemplate({ ...editingTemplate, icon_id: icon.id })}
                                className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center text-xl ${
                                  editingTemplate.icon_id === icon.id 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-slate-200 hover:border-slate-300'
                                }`}
                                title={icon.name}
                              >
                                {icon.icon}
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        {/* Color picker */}
                        <div>
                          <label className="text-sm font-medium">Header-färg</label>
                          <div className="grid grid-cols-4 gap-2 mt-2">
                            {TEMPLATE_COLORS.map((color) => (
                              <button
                                key={color.id}
                                onClick={() => setEditingTemplate({ ...editingTemplate, header_color: color.id })}
                                className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                                  editingTemplate.header_color === color.id 
                                    ? 'border-slate-800' 
                                    : 'border-transparent hover:border-slate-300'
                                }`}
                              >
                                <div 
                                  className="w-6 h-6 rounded-full" 
                                  style={{ background: color.color }}
                                />
                                <span className="text-sm">{color.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium">Meddelande</label>
                          <div className="mt-1">
                            <RichTextEditor
                              content={editingTemplate.body_html}
                              onChange={(html) => setEditingTemplate({ ...editingTemplate, body_html: html })}
                              placeholder="Skriv mallens standardinnehåll här..."
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Right column: Preview */}
                      <div>
                        <label className="text-sm font-medium mb-2 block">Förhandsgranskning</label>
                        <div className="border rounded-lg overflow-hidden bg-slate-100 h-[500px] overflow-y-auto">
                          <div 
                            dangerouslySetInnerHTML={{ 
                              __html: generateTemplateHtml(
                                editingTemplate, 
                                editingTemplate.body_html || 'Ditt meddelande kommer visas här...',
                                user?.name || user?.email
                              ) 
                            }} 
                          />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                  <div className="flex justify-end gap-2 p-4 border-t bg-slate-50">
                    <Button variant="outline" onClick={() => setEditingTemplate(null)}>
                      Avbryt
                    </Button>
                    <Button onClick={saveTemplate} className="bg-blue-600 hover:bg-blue-700">
                      Spara mall
                    </Button>
                  </div>
                </Card>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
